import { word } from "@modules/core/utils/i18n"
import { ProgramBundleSubscriberEntity } from "@modules/programBundle/programBundleSubscribers/entities/programBundleSubscriberEntity"
import {
  ProgramSubscriberDeleteModal,
  ProgramSubscriberDeleteModalRef,
} from "@modules/programs/programSubscriber/components/programSubscriberDeleteModal"
import { ProgramSubscriberDetailsViewTabs } from "@modules/programs/programSubscriber/entities/programSubsciberType"
import { ProgramSubscriberEntity } from "@modules/programs/programSubscriber/entities/programSubscriberEntity"
import { useProgramSubscriberActions } from "@modules/programs/programSubscriber/hooks/useProgramSubscriber"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { EllipsisMenu } from "@modules/ui/components/ellipsisMenu"
import { useToast } from "@modules/ui/components/huToast"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { BookTextIcon, Pause, Percent, Play, SquareMinus } from "lucide-react"
import { MenuItem } from "primereact/menuitem"
import { useRef } from "react"
import { useTheme } from "styled-components"

type ProgramSubscriberDetailsMenuProps = {
  subscriberDetails: ProgramSubscriberEntity | ProgramBundleSubscriberEntity
  setActiveTab: (tab: ProgramSubscriberDetailsViewTabs) => void
  programId: string
} & React.ComponentProps<"div">

export function ProgramSubscriberDetailsMenu({
  subscriberDetails,
  setActiveTab,
  programId,
  ...props
}: ProgramSubscriberDetailsMenuProps) {
  const theme = useTheme()
  const toast = useToast()
  const programSubscriberDeleteModalRef = useRef<ProgramSubscriberDeleteModalRef>(null)
  const popup = useConfirmPopup()
  const { sidebarNavigateBack } = useSidebar()
  const { removeSubscriber } = useProgramSubscriberActions(programId, subscriberDetails)

  const onRemoveSubscriberClick = () => {
    if (subscriberDetails.paidPrice && subscriberDetails.paidPrice.value > 0) {
      programSubscriberDeleteModalRef.current?.open()
    } else {
      popup.show({
        title: "",
        message: word("program.subscribers.subscriptionDetails.remove.title"),
        accept: async () => {
          await removeSubscriber(false)
            .then(() => {
              toast.show({
                severity: "success",
                summary: word("program.subscribers.subscriptionDetails.remove.success"),
              })
              sidebarNavigateBack()
            })
            .catch(() => {
              toast.show({
                severity: "error",
                summary: word("program.subscribers.subscriptionDetails.remove.error"),
              })
            })
        },
        footerProps: {
          align: true,
          confirmText: word("global.delete"),
          confirmClassName: "flex-1",
          cancelClassName: "flex-1",
          footerClassName: "flex flex-row justify-content-around w-full",
        },
      })
    }
  }

  const ellipsisItems: MenuItem[] = [
    {
      items: [
        {
          label: subscriberDetails.pause
            ? word("program.subscribers.subscriptionDetails.pause.resume")
            : word("program.subscribers.subscriptionDetails.pause.suspend"),
          icon: subscriberDetails.pause ? (
            <Play color={theme.textPrimary} strokeWidth={1} size={16} />
          ) : (
            <Pause color={theme.textPrimary} strokeWidth={1} size={16} />
          ),
          command: () => {
            setActiveTab(ProgramSubscriberDetailsViewTabs.PAUSE_FORM)
          },
          visible: subscriberDetails.canSubscriptionBePaused(),
        },
        {
          label: subscriberDetails.coupon
            ? word("program.subscribers.subscriptionDetails.coupon.modify")
            : word("program.subscribers.subscriptionDetails.coupon.create"),
          icon: <Percent color={theme.textPrimary} strokeWidth={1} size={16} />,
          command: () => {
            setActiveTab(ProgramSubscriberDetailsViewTabs.DISCOUNT_FORM)
          },
          visible: subscriberDetails.canApplyCoupon(),
        },
        {
          label: subscriberDetails.internalNote
            ? word("program.subscribers.subscriptionDetails.internalNote.form.editTitle")
            : word("program.subscribers.subscriptionDetails.internalNote.form.createTitle"),
          icon: <BookTextIcon color={theme.textPrimary} strokeWidth={1} size={16} />,
          command: () => {
            setActiveTab(ProgramSubscriberDetailsViewTabs.INTERNAL_NOTE_FORM)
          },
          visible: true,
        },
        {
          label:
            subscriberDetails instanceof ProgramBundleSubscriberEntity
              ? word("programBundle.subscribers.subscriptionDetails.remove.buttonLabel")
              : word("program.subscribers.subscriptionDetails.remove.buttonLabel"),
          icon: <SquareMinus color={theme.textPrimary} strokeWidth={1} size={16} />,
          template: () => (
            <div
              className="flex text-error gap-1 cursor-pointer hover:surface-100 transition-colors transition-duration-150"
              style={{
                paddingRight: "1.25rem",
                paddingLeft: "1.25rem",
                paddingTop: "0.75rem",
                paddingBottom: "0.75rem",
                borderRadius: "5px",
              }}
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                onRemoveSubscriberClick()
              }}
            >
              <SquareMinus color={theme.error} strokeWidth={1} size={16} />
              <span style={{ marginLeft: "8px" }}>{word("box.member.table.offer.item.menu.removeSubscription")}</span>
            </div>
          ),
          visible: subscriberDetails.canDeleteSubscriber(),
        },
      ],
    },
  ]

  return (
    <>
      <EllipsisMenu
        items={ellipsisItems}
        width={300}
        {...props}
        style={{ backgroundColor: theme.sidebarHeaderButtonsColor }}
        buttonClassName="h-2rem w-2rem mr-2 border-none-hover"
      />
      <ProgramSubscriberDeleteModal
        ref={programSubscriberDeleteModalRef}
        subscriberDetails={subscriberDetails}
        programId={programId}
      />
    </>
  )
}
